import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Refresh,
  Search,
  SwapVertOutlined,
  PeopleAltOutlined,
  ErrorOutline,
  SpeedOutlined,
  LoginOutlined,
  LogoutOutlined,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import PillButtonGroup from "../../components/PillButtonGroup";
import {
  fetchUsageEndpoints,
  fetchUsageRequests,
  fetchUsageSummary,
  fetchUsageTimeseries,
  fetchUsageUsers,
  fetchUsageSlow,
  fetchUsageAuthFunnel,
} from "../../Services/usageService";
import {
  C,
  FONT,
  MONO,
  cardSx,
  ROLE_ORDER,
  ROLE_COLORS,
  ROLE_LABELS,
  roleKey,
  categoryLabel,
  formatNumber,
  formatMs,
  formatDateTime,
  formatTimeAgo,
} from "./usage/usageTheme";
import { MethodPill, StatusPill, RolePill, EndpointText, CategoryPill } from "./usage/UsageBits";
import { IssuesCard, WebhookCard, AuthFunnelCard, SlowRequestsCard } from "./usage/HealthPanels";

const RANGES = [
  { id: "today", label: "Today" },
  { id: "24h", label: "24 hours" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "custom", label: "Custom" },
];

const TABS = [
  { id: "endpoints", label: "Endpoints" },
  { id: "users", label: "Users" },
  { id: "requests", label: "Requests" },
  { id: "errors", label: "Errors" },
];

const toDateInput = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Turns the selected range into the API's from/to + chart bucket size.
function resolveRange(range, customFrom, customTo) {
  const now = new Date();
  if (range === "today") {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return { from: start.toISOString(), to: null, bucket: "hour" };
  }
  if (range === "24h") return { from: new Date(now - 24 * 3600e3).toISOString(), to: null, bucket: "hour" };
  if (range === "7d") return { from: new Date(now - 7 * 86400e3).toISOString(), to: null, bucket: "day" };
  if (range === "30d") return { from: new Date(now - 30 * 86400e3).toISOString(), to: null, bucket: "day" };
  const from = customFrom ? new Date(`${customFrom}T00:00:00`) : new Date(now - 7 * 86400e3);
  const to = customTo ? new Date(`${customTo}T23:59:59.999`) : null;
  const spanDays = ((to || now) - from) / 86400e3;
  return { from: from.toISOString(), to: to ? to.toISOString() : null, bucket: spanDays <= 2 ? "hour" : "day" };
}

const pillButtonSx = (active) => ({
  px: 1.75,
  py: 0.5,
  minWidth: 0,
  fontSize: "0.8125rem",
  fontWeight: active ? 600 : 400,
  color: active ? "#FFFFFF" : C.textBody,
  bgcolor: active ? C.primary : "transparent",
  "&:hover": { bgcolor: active ? C.primary : C.gray7 },
});

const selectSx = {
  minWidth: 140,
  "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: "0.8125rem", bgcolor: "#FFFFFF" },
  "& .MuiInputLabel-root": { fontSize: "0.8125rem" },
};

function SectionTitle({ title, subtitle, right }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2 }}>
      <Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: 12.5, color: C.textSecondary, mt: "2px" }}>{subtitle}</Typography>}
      </Box>
      {right}
    </Box>
  );
}

function StatCard({ icon, label, value, hint, tone }) {
  return (
    <Box sx={{ ...cardSx, p: "20px 22px", display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "9px",
            background: tone === "error" ? C.errorBg : C.gray7,
            color: tone === "error" ? C.error : C.ink,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
      </Box>
      <Box>
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: tone === "error" ? C.error : C.ink, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
          {value}
        </Typography>
        <Typography sx={{ fontSize: 13, color: C.textSecondary, mt: "2px" }}>{label}</Typography>
        {hint && <Typography sx={{ fontSize: 11.5, color: C.textFaint, mt: "2px" }}>{hint}</Typography>}
      </Box>
    </Box>
  );
}

function EmptyNote({ children }) {
  return <Typography sx={{ fontSize: 13, color: C.textFaint, textAlign: "center", py: 4 }}>{children}</Typography>;
}

// Ranked horizontal bars (top endpoints / origins) — label, count, and a
// proportional bar, the same ranked-list style as the admin Dashboard.
function RankedBars({ rows, labelKey, valueKey, renderLabel, onClick, color = C.primary }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  if (rows.length === 0) return <EmptyNote>No calls in this range.</EmptyNote>;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {rows.map((r) => (
        <Box
          key={`${r[labelKey]}-${r.method || ""}`}
          onClick={onClick ? () => onClick(r) : undefined}
          sx={{ cursor: onClick ? "pointer" : "default", borderRadius: "10px", px: 1, py: 0.75, mx: -1, "&:hover": onClick ? { background: "#FAFAFA" } : {} }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.75, alignItems: "center" }}>
            <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}>{renderLabel ? renderLabel(r) : r[labelKey]}</Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.ink, flexShrink: 0 }}>{formatNumber(r[valueKey])}</Typography>
          </Box>
          <Box sx={{ height: 6, borderRadius: 3, background: C.gray7, overflow: "hidden" }}>
            <Box sx={{ height: "100%", width: `${(r[valueKey] / max) * 100}%`, background: color, borderRadius: 3 }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function CallsOverTime({ points, bucket }) {
  const data = points.map((p) => ({
    ...p,
    label: new Date(p.bucket).toLocaleString("en-GB", bucket === "hour" ? { hour: "2-digit", minute: "2-digit" } : { day: "2-digit", month: "short" }),
  }));
  if (data.length === 0) return <EmptyNote>No calls in this range.</EmptyNote>;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
        <CartesianGrid stroke={C.gray6} vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.textSecondary }} axisLine={false} tickLine={false} minTickGap={16} />
        <YAxis tick={{ fontSize: 11, fill: C.textSecondary }} axisLine={false} tickLine={false} allowDecimals={false} />
        <ChartTooltip
          contentStyle={{ borderRadius: 12, border: `1px solid ${C.border}`, fontSize: 12, fontFamily: FONT }}
          formatter={(value, name) => [formatNumber(value), ROLE_LABELS[name] || name]}
        />
        {ROLE_ORDER.map((role) => (
          <Area
            key={role}
            type="monotone"
            dataKey={role}
            stackId="calls"
            stroke={ROLE_COLORS[role]}
            fill={ROLE_COLORS[role]}
            fillOpacity={0.85}
            strokeWidth={0}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function RoleDonut({ byRole: rawByRole, total }) {
  // Combined labels ("therapist+patient") are summed into one "mixed" slice.
  const byRole = Object.values(
    rawByRole.reduce((acc, r) => {
      const key = roleKey(r.role);
      acc[key] = acc[key] || { role: key, calls: 0, users: 0 };
      acc[key].calls += r.calls;
      acc[key].users += r.users;
      return acc;
    }, {})
  );
  const data = ROLE_ORDER.map((role) => ({ role, calls: byRole.find((r) => r.role === role)?.calls || 0 })).filter((d) => d.calls > 0);
  if (data.length === 0) return <EmptyNote>No calls in this range.</EmptyNote>;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      <Box sx={{ width: 150, height: 150, position: "relative", flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="calls" nameKey="role" innerRadius={48} outerRadius={72} paddingAngle={2} stroke="none">
              {data.map((d) => (
                <Cell key={d.role} fill={ROLE_COLORS[d.role]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: C.ink }}>{formatNumber(total)}</Typography>
          <Typography sx={{ fontSize: 11, color: C.textSecondary }}>calls</Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 140, display: "flex", flexDirection: "column", gap: 1 }}>
        {data.map((d) => {
          const row = byRole.find((r) => r.role === d.role) || {};
          return (
            <Box key={d.role} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 9, height: 9, borderRadius: "50%", background: ROLE_COLORS[d.role] }} />
                <Typography sx={{ fontSize: 13, color: C.textBody }}>{ROLE_LABELS[d.role]}</Typography>
              </Box>
              <Typography sx={{ fontSize: 12.5, color: C.ink, fontWeight: 600 }}>
                {formatNumber(d.calls)}
                {d.role !== "guest" && (
                  <Box component="span" sx={{ color: C.textFaint, fontWeight: 400 }}>
                    {" "}· {formatNumber(row.users)} {row.users === 1 ? "user" : "users"}
                  </Box>
                )}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

const headCellSx = { fontSize: 11.5, fontWeight: 700, color: C.textSecondary, textTransform: "uppercase", letterSpacing: "0.04em", borderColor: C.gray6, py: 1.25 };
const bodyCellSx = { fontSize: 13, color: C.textBody, borderColor: C.gray6, py: 1.25 };

function EndpointsTable({ rows, onSelect }) {
  if (rows.length === 0) return <EmptyNote>No calls in this range.</EmptyNote>;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>Endpoint</TableCell>
            <TableCell sx={headCellSx} align="right">Calls</TableCell>
            <TableCell sx={headCellSx} align="right">Errors</TableCell>
            <TableCell sx={headCellSx} align="right">Users</TableCell>
            <TableCell sx={headCellSx} align="right">Avg</TableCell>
            <TableCell sx={headCellSx} align="right">P95</TableCell>
            <TableCell sx={headCellSx} align="right">Last called</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${r.method} ${r.endpoint}`} hover onClick={() => onSelect(r)} sx={{ cursor: "pointer" }}>
              <TableCell sx={bodyCellSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <MethodPill method={r.method} />
                  <EndpointText endpoint={r.endpoint} />
                </Box>
              </TableCell>
              <TableCell sx={{ ...bodyCellSx, fontWeight: 700, color: C.ink }} align="right">{formatNumber(r.calls)}</TableCell>
              <TableCell sx={{ ...bodyCellSx, color: r.errors ? C.error : C.textFaint, fontWeight: r.errors ? 700 : 400 }} align="right">
                {formatNumber(r.errors)}
              </TableCell>
              <TableCell sx={bodyCellSx} align="right">{formatNumber(r.users)}</TableCell>
              <TableCell sx={bodyCellSx} align="right">{formatMs(r.avg_ms)}</TableCell>
              <TableCell sx={bodyCellSx} align="right">{formatMs(r.p95_ms)}</TableCell>
              <TableCell sx={{ ...bodyCellSx, color: C.textSecondary }} align="right">{formatTimeAgo(r.last_called)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function UsersTable({ rows, onSelect }) {
  if (rows.length === 0) return <EmptyNote>No signed-in users in this range.</EmptyNote>;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>User</TableCell>
            <TableCell sx={headCellSx}>Role</TableCell>
            <TableCell sx={headCellSx} align="right">Calls</TableCell>
            <TableCell sx={headCellSx} align="right">Errors</TableCell>
            <TableCell sx={headCellSx} align="right">Endpoints used</TableCell>
            <TableCell sx={headCellSx} align="right">Last seen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.user_id} hover onClick={() => onSelect(r)} sx={{ cursor: "pointer" }}>
              <TableCell sx={{ ...bodyCellSx, color: C.ink, fontWeight: 600 }}>
                {r.user_email}
                <Box component="span" sx={{ color: C.textFaint, fontWeight: 400, ml: 1, fontSize: 11.5 }}>#{r.user_id}</Box>
              </TableCell>
              <TableCell sx={bodyCellSx}><RolePill role={r.role} /></TableCell>
              <TableCell sx={{ ...bodyCellSx, fontWeight: 700, color: C.ink }} align="right">{formatNumber(r.calls)}</TableCell>
              <TableCell sx={{ ...bodyCellSx, color: r.errors ? C.error : C.textFaint, fontWeight: r.errors ? 700 : 400 }} align="right">
                {formatNumber(r.errors)}
              </TableCell>
              <TableCell sx={bodyCellSx} align="right">{formatNumber(r.endpoints)}</TableCell>
              <TableCell sx={{ ...bodyCellSx, color: C.textSecondary }} align="right">{formatTimeAgo(r.last_seen)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// errorScope: null (all requests) | 'error' (real problems) | 'expected' |
// 'anyerror'; category narrows to one failure cause.
function RequestsGrid({ baseFilters, errorScope = null, category = null }) {
  const errorsOnly = Boolean(errorScope);
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 25 });
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  // Debounced search, and back to page 1 whenever the filter set changes.
  React.useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(id);
  }, [searchInput]);
  const filterKey = JSON.stringify({ ...baseFilters, errorScope, category, search });
  React.useEffect(() => {
    setPaginationModel((m) => ({ ...m, page: 0 }));
  }, [filterKey]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchUsageRequests({
      ...baseFilters,
      ...(errorScope ? { status: errorScope } : {}),
      ...(category ? { category } : {}),
      search,
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
    })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data.requests);
        setTotal(res.data.total);
      })
      .catch((err) => !cancelled && setError(err.message || "Failed to load requests"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, paginationModel.page, paginationModel.pageSize]);

  const columns = [
    { field: "created_at", headerName: "Time", width: 150, sortable: false, renderCell: (p) => <Box sx={{ fontSize: 12.5, color: C.textBody }}>{formatDateTime(p.value)}</Box> },
    {
      field: "endpoint",
      headerName: "Endpoint",
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%", minWidth: 0 }}>
          <MethodPill method={p.row.method} />
          <Tooltip title={p.row.path} placement="top-start">
            <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <EndpointText endpoint={p.value} />
            </Box>
          </Tooltip>
        </Box>
      ),
    },
    { field: "status_code", headerName: "Status", width: 80, sortable: false, renderCell: (p) => <StatusPill code={p.value} /> },
    { field: "duration_ms", headerName: "Duration", width: 95, sortable: false, renderCell: (p) => <Box sx={{ fontSize: 12.5, fontFamily: MONO }}>{formatMs(p.value)}</Box> },
    {
      field: "user_email",
      headerName: "User",
      width: 230,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%", minWidth: 0 }}>
          <RolePill role={p.row.user_role} />
          <Box sx={{ fontSize: 12.5, color: C.textBody, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.value || "—"}</Box>
        </Box>
      ),
    },
    { field: "origin", headerName: "Origin", width: 120, sortable: false, renderCell: (p) => <Box sx={{ fontSize: 12.5, color: C.textSecondary }}>{p.value}</Box> },
    ...(errorsOnly
      ? [
          { field: "error_category", headerName: "Cause", width: 200, sortable: false, renderCell: (p) => <CategoryPill category={p.value} /> },
          {
            field: "error_message",
            headerName: "Message",
            flex: 1,
            minWidth: 180,
            sortable: false,
            renderCell: (p) => (
              <Tooltip title={p.value || ""} placement="top-start">
                <Box sx={{ fontSize: 12.5, color: p.row.is_expected ? C.textSecondary : C.error, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.value}</Box>
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  return (
    <Box>
      <TextField
        size="small"
        placeholder="Search path, endpoint, email or error…"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        sx={{ ...selectSx, width: { xs: "100%", sm: 360 }, mb: 2 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: C.textFaint }} /></InputAdornment> }}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <DataGrid
        rows={rows}
        columns={columns}
        rowCount={total}
        loading={loading}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[25, 50, 100]}
        disableColumnMenu
        disableRowSelectionOnClick
        onRowClick={(p) => navigate(`/usage/requests/${p.id}`)}
        rowHeight={48}
        autoHeight
        sx={{
          border: "none",
          fontFamily: FONT,
          "& .MuiDataGrid-columnHeaders": { borderBottom: `1px solid ${C.gray6}` },
          "& .MuiDataGrid-columnHeaderTitle": { fontSize: 11.5, fontWeight: 700, color: C.textSecondary, textTransform: "uppercase", letterSpacing: "0.04em" },
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-cell": { display: "flex", alignItems: "center", borderColor: C.gray6 },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
        }}
      />
    </Box>
  );
}

export default function Usage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [range, setRange] = React.useState("today");
  const [customFrom, setCustomFrom] = React.useState(toDateInput(new Date(Date.now() - 7 * 86400e3)));
  const [customTo, setCustomTo] = React.useState(toDateInput(new Date()));
  const [role, setRole] = React.useState("all");
  const [origin, setOrigin] = React.useState("all");
  // portal = real portal traffic (default); blocked = gatekeeper-rejected bot calls
  const [traffic, setTraffic] = React.useState("portal");
  const [endpointFilter, setEndpointFilter] = React.useState(null); // { method, endpoint }
  const [userFilter, setUserFilter] = React.useState(null); // { id, email }
  const [refreshTick, setRefreshTick] = React.useState(0);
  // Errors tab: real problems by default; expected = normal-operation failures
  const [errorScope, setErrorScope] = React.useState("error");
  const [errorCategory, setErrorCategory] = React.useState(null);
  const [slow, setSlow] = React.useState([]);
  const [authFunnel, setAuthFunnel] = React.useState(null);
  const tab = TABS.some((t) => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "endpoints";
  const setTab = (id) => setSearchParams((p) => { p.set("tab", id); return p; }, { replace: true });

  const [summary, setSummary] = React.useState(null);
  const [timeseries, setTimeseries] = React.useState({ bucket: "hour", points: [] });
  const [endpoints, setEndpoints] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [lastUpdated, setLastUpdated] = React.useState(null);

  const { from, to, bucket } = React.useMemo(
    () => resolveRange(range, customFrom, customTo),
    // refreshTick re-resolves "now" for rolling ranges on refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [range, customFrom, customTo, refreshTick]
  );
  const baseFilters = React.useMemo(
    () => ({
      from,
      to,
      role,
      origin,
      traffic,
      endpoint: endpointFilter?.endpoint,
      method: endpointFilter?.method,
      userId: userFilter?.id,
    }),
    [from, to, role, origin, traffic, endpointFilter, userFilter]
  );

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([
      fetchUsageSummary(baseFilters),
      fetchUsageTimeseries({ ...baseFilters, bucket }),
      fetchUsageEndpoints(baseFilters),
      fetchUsageUsers(baseFilters),
      fetchUsageSlow(baseFilters),
      // The funnel is per person/email, so only the date range applies.
      fetchUsageAuthFunnel({ from, to }),
    ])
      .then(([s, ts, e, u, sl, af]) => {
        if (cancelled) return;
        setSummary(s.data);
        setTimeseries(ts.data);
        setEndpoints(e.data);
        setUsers(u.data);
        setSlow(sl.data);
        setAuthFunnel(af.data);
        setLastUpdated(new Date());
      })
      .catch((err) => !cancelled && setError(err.message || "Failed to load usage data"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [baseFilters, bucket, from, to]);

  const origins = summary?.byOrigin || [];
  const errorRate = summary && summary.total_calls ? ((summary.error_calls / summary.total_calls) * 100).toFixed(1) : "0.0";

  const selectEndpoint = (r) => {
    setEndpointFilter({ method: r.method, endpoint: r.endpoint });
    setTab("requests");
  };
  const selectCategory = (c) => {
    setErrorScope(c.expected ? "expected" : "error");
    setErrorCategory(c.category);
    setTab("errors");
  };
  const selectUser = (r) => {
    setUserFilter({ id: r.user_id, email: r.user_email });
    setTab("requests");
  };

  return (
    <Box sx={{ fontFamily: FONT, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", md: "row" }, gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" }}>API Usage</Typography>
          <Typography sx={{ fontSize: 13, color: C.textSecondary, mt: "2px" }}>
            Every API call across the portal, by endpoint, user and origin.
            {lastUpdated && ` Updated ${lastUpdated.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}.`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <PillButtonGroup>
            {RANGES.map((r) => (
              <Button key={r.id} variant="text" onClick={() => setRange(r.id)} sx={pillButtonSx(range === r.id)}>
                {r.label}
              </Button>
            ))}
          </PillButtonGroup>
          <Tooltip title="Refresh">
            <IconButton onClick={() => setRefreshTick((t) => t + 1)} size="small" sx={{ bgcolor: "#FFFFFF", border: "1px solid #e0e0e0", borderRadius: 1.5 }}>
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 3 }}>
        {range === "custom" && (
          <>
            <TextField type="date" size="small" label="From" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={selectSx} />
            <TextField type="date" size="small" label="To" value={customTo} onChange={(e) => setCustomTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={selectSx} />
          </>
        )}
        <TextField select size="small" label="Traffic" value={traffic} onChange={(e) => setTraffic(e.target.value)} sx={{ ...selectSx, minWidth: 170 }}>
          <MenuItem value="portal">Portal traffic</MenuItem>
          <MenuItem value="blocked">Blocked (bots)</MenuItem>
          <MenuItem value="all">All traffic</MenuItem>
        </TextField>
        <TextField select size="small" label="Role" value={role} onChange={(e) => setRole(e.target.value)} sx={selectSx}>
          <MenuItem value="all">All roles</MenuItem>
          {ROLE_ORDER.map((r) => (
            <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Origin" value={origin} onChange={(e) => setOrigin(e.target.value)} sx={{ ...selectSx, minWidth: 190 }}>
          <MenuItem value="all">All origins</MenuItem>
          {origins.map((o) => (
            <MenuItem key={o.origin} value={o.origin}>{o.origin}</MenuItem>
          ))}
          {origin !== "all" && !origins.some((o) => o.origin === origin) && <MenuItem value={origin}>{origin}</MenuItem>}
        </TextField>
        {endpointFilter && (
          <Chip
            label={<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}><MethodPill method={endpointFilter.method} /><EndpointText endpoint={endpointFilter.endpoint} /></Box>}
            onDelete={() => setEndpointFilter(null)}
            sx={{ bgcolor: "#FFFFFF", border: "1px solid #e0e0e0", height: 34 }}
          />
        )}
        {userFilter && (
          <Chip
            label={`User: ${userFilter.email || `#${userFilter.id}`}`}
            onDelete={() => setUserFilter(null)}
            sx={{ bgcolor: "#FFFFFF", border: "1px solid #e0e0e0", height: 34, fontSize: 13 }}
          />
        )}
        {loading && <CircularProgress size={18} sx={{ color: C.gray1 }} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Stat cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" }, gap: 2, mb: 2 }}>
        <StatCard
          icon={<SwapVertOutlined sx={{ fontSize: 18 }} />}
          label={traffic === "blocked" ? "Blocked calls" : "API calls"}
          value={formatNumber(summary?.total_calls)}
          hint={traffic === "portal" && summary?.blocked_calls ? `${formatNumber(summary.blocked_calls)} blocked bot ${summary.blocked_calls === 1 ? "call" : "calls"} hidden` : null}
        />
        <StatCard
          icon={<PeopleAltOutlined sx={{ fontSize: 18 }} />}
          label="Signed-in users"
          value={formatNumber(summary?.unique_users)}
          hint={summary ? `+ ${formatNumber(summary.guest_visitors)} guest ${summary.guest_visitors === 1 ? "IP" : "IPs"}` : null}
        />
        <StatCard
          icon={<ErrorOutline sx={{ fontSize: 18 }} />}
          label="Real errors"
          value={`${errorRate}%`}
          hint={summary ? `${formatNumber(summary.error_calls)} ${summary.error_calls === 1 ? "problem" : "problems"} · ${formatNumber(summary.expected_calls)} expected` : null}
          tone={Number(errorRate) >= 1 ? "error" : undefined}
        />
        <StatCard
          icon={<SpeedOutlined sx={{ fontSize: 18 }} />}
          label="Avg response"
          value={formatMs(summary?.avg_ms)}
          hint={summary ? `95% faster than ${formatMs(summary.p95_ms)}` : null}
        />
        <StatCard
          icon={<LoginOutlined sx={{ fontSize: 18 }} />}
          label="Logins"
          value={formatNumber(summary?.successful_logins)}
          hint={summary ? `${formatNumber(summary.failed_logins)} failed ${summary.failed_logins === 1 ? "attempt" : "attempts"}` : null}
          tone={summary && summary.failed_logins > summary.successful_logins ? "error" : undefined}
        />
        <StatCard
          icon={<LogoutOutlined sx={{ fontSize: 18 }} />}
          label="Sessions expired"
          value={formatNumber(summary?.sessions_expired)}
          hint="Users who had to log in again"
          tone={summary && summary.sessions_expired >= 10 ? "error" : undefined}
        />
      </Box>

      {/* Charts */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 2, mb: 2 }}>
        <Box sx={{ ...cardSx, p: 3, minWidth: 0 }}>
          <SectionTitle title="Calls over time" subtitle={`Per ${timeseries.bucket}, stacked by role`} />
          <CallsOverTime points={timeseries.points} bucket={timeseries.bucket} />
        </Box>
        <Box sx={{ ...cardSx, p: 3, minWidth: 0 }}>
          <SectionTitle title="Who is calling" subtitle="Calls by role" />
          <RoleDonut byRole={summary?.byRole || []} total={summary?.total_calls || 0} />
        </Box>
      </Box>

      {traffic !== "blocked" && (
        <>
          {/* Stretch: the left card fills the row height of the right column
              (webhooks + origins), so the two sides line up. */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" }, gap: 2, mb: 2, alignItems: "stretch" }}>
            <IssuesCard byCategory={summary?.byCategory || []} onSelect={selectCategory} sx={{ height: "100%", boxSizing: "border-box" }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <WebhookCard webhooks={summary?.webhooks} />
              <Box sx={{ ...cardSx, p: 3, minWidth: 0 }}>
                <SectionTitle title="Origins" subtitle="Where calls come from — click to filter" />
                <RankedBars
                  rows={origins.slice(0, 6)}
                  labelKey="origin"
                  valueKey="calls"
                  color={C.tint1}
                  onClick={(r) => setOrigin(r.origin)}
                  renderLabel={(r) => <Typography sx={{ fontSize: 13, color: C.textBody }}>{r.origin}</Typography>}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <AuthFunnelCard data={authFunnel} />
          </Box>
        </>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" }, gap: 2, mb: 2, alignItems: "start" }}>
        <Box sx={{ ...cardSx, p: 3, minWidth: 0 }}>
          <SectionTitle title="Top endpoints" subtitle="Most called — click to see the requests" />
          <RankedBars
            rows={endpoints.slice(0, 8)}
            labelKey="endpoint"
            valueKey="calls"
            onClick={selectEndpoint}
            renderLabel={(r) => (
              <>
                <MethodPill method={r.method} />
                <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <EndpointText endpoint={r.endpoint} />
                </Box>
              </>
            )}
          />
        </Box>
        <SlowRequestsCard rows={slow} />
      </Box>

      {/* Tabs */}
      <Box sx={{ ...cardSx, p: 3, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
          <PillButtonGroup>
            {TABS.map((t) => (
              <Button key={t.id} variant="text" onClick={() => setTab(t.id)} sx={pillButtonSx(tab === t.id)}>
                {t.label}
                {t.id === "errors" && summary?.error_calls ? ` (${formatNumber(summary.error_calls)})` : ""}
              </Button>
            ))}
          </PillButtonGroup>
          <Typography sx={{ fontSize: 12, color: C.textFaint }}>
            {tab === "endpoints" && `${endpoints.length} endpoints`}
            {tab === "users" && `${users.length} users`}
          </Typography>
        </Box>

        {tab === "endpoints" && <EndpointsTable rows={endpoints} onSelect={selectEndpoint} />}
        {tab === "users" && <UsersTable rows={users} onSelect={selectUser} />}
        {tab === "requests" && <RequestsGrid baseFilters={baseFilters} />}
        {tab === "errors" && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
              <PillButtonGroup>
                {[
                  { id: "error", label: "Real problems" },
                  { id: "expected", label: "Expected" },
                  { id: "anyerror", label: "All failures" },
                ].map((s) => (
                  <Button key={s.id} variant="text" onClick={() => setErrorScope(s.id)} sx={pillButtonSx(errorScope === s.id)}>
                    {s.label}
                  </Button>
                ))}
              </PillButtonGroup>
              {errorCategory && (
                <Chip
                  label={`Cause: ${categoryLabel(errorCategory)}`}
                  onDelete={() => setErrorCategory(null)}
                  sx={{ bgcolor: "#FFFFFF", border: "1px solid #e0e0e0", height: 32, fontSize: 13 }}
                />
              )}
            </Box>
            <RequestsGrid baseFilters={baseFilters} errorScope={errorScope} category={errorCategory} />
          </>
        )}
      </Box>
    </Box>
  );
}
